import './ui.css';

// A server's campfire: the pixel fire that shows its state at a glance (the app's one motif).
// Drawn as data on a 7×10 grid, so the states are easy to compare and test.

export type CampfireState =
  /** Running: a full flame, swaying. */
  | 'lit'
  /** Waking up: a small flame, flickering. */
  | 'catching'
  /** Falling asleep: a small flame, dimming. */
  | 'fading'
  /** Asleep: grey smoke over two glowing embers. */
  | 'embers'
  /** No server yet: cold logs and smoke. */
  | 'unlit';

/** One pixel run: x, y, width, height, colour. */
export type Pixel = readonly [number, number, number, number, string];

export interface Layer {
  pixels: readonly Pixel[];
  /** A class for this layer alone (e.g. the embers' glow). */
  className?: string;
}

const EMBER = '#ff8a3d';
const GLOW = '#ffc46b';
const CORE = '#fff1c9';
const SMOKE = '#3a4256';

const LOGS: Layer = { pixels: [[0, 8, 7, 1, '#7a4a2a'], [1, 9, 2, 1, '#4e2f1b'], [4, 9, 2, 1, '#4e2f1b']] };
const COLD_LOGS: Layer = { pixels: [[0, 8, 7, 1, '#5a3a24'], [1, 9, 2, 1, '#3d2616'], [4, 9, 2, 1, '#3d2616']] };
const BIG_FLAME: Layer = {
  pixels: [
    [3, 0, 1, 1, EMBER], [2, 1, 2, 1, EMBER], [2, 2, 3, 1, EMBER], [1, 3, 4, 1, EMBER], [1, 4, 5, 1, EMBER],
    [0, 5, 6, 1, EMBER], [0, 6, 7, 1, EMBER], [1, 7, 5, 1, EMBER],
    [2, 3, 2, 1, GLOW], [2, 4, 3, 3, GLOW], [2, 7, 3, 1, GLOW],
    [3, 5, 1, 2, CORE],
  ],
};
const SMALL_FLAME: Layer = {
  pixels: [
    [3, 4, 1, 1, EMBER], [2, 5, 2, 1, EMBER], [2, 6, 3, 1, EMBER], [1, 7, 5, 1, EMBER],
    [3, 6, 1, 1, GLOW], [2, 7, 3, 1, GLOW],
  ],
};
const DRIFTING_SMOKE: Layer = { pixels: [[3, 2, 1, 1, SMOKE], [2, 3, 1, 1, SMOKE], [3, 4, 1, 1, SMOKE], [4, 5, 1, 1, SMOKE]] };
const EMBERS: Layer = { pixels: [[2, 7, 1, 1, '#b65316'], [4, 7, 1, 1, '#b65316']], className: 'campfire-glow' };
const COLD_SMOKE: Layer = { pixels: [[3, 4, 1, 1, SMOKE], [2, 5, 1, 1, SMOKE], [4, 5, 1, 1, SMOKE], [3, 6, 1, 1, SMOKE], [1, 7, 5, 1, '#5a3a24']] };

export const CAMPFIRE_LAYERS: Readonly<Record<CampfireState, readonly Layer[]>> = {
  lit: [BIG_FLAME, LOGS],
  catching: [SMALL_FLAME, LOGS],
  fading: [SMALL_FLAME, LOGS],
  embers: [DRIFTING_SMOKE, EMBERS, COLD_LOGS],
  unlit: [COLD_SMOKE, COLD_LOGS],
};

/**
 * The campfire for a state, `height` pixels tall. Decorative: the state is always said in words
 * next to it. `still` drops the state's own motion (e.g. the Wake button, which moves on hover).
 */
export function Campfire({ state, height = 40, still = false }: { state: CampfireState; height?: number; still?: boolean }) {
  return (
    <svg
      className={still ? 'campfire' : `campfire campfire-${state}`}
      width={(height * 7) / 10}
      height={height}
      viewBox="0 0 7 10"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {CAMPFIRE_LAYERS[state].map((layer, i) => (
        <g key={i} className={layer.className}>
          {layer.pixels.map(([x, y, w, h, fill], j) => (
            <rect key={j} x={x} y={y} width={w} height={h} fill={fill} />
          ))}
        </g>
      ))}
    </svg>
  );
}
